package com.rosario.juridico.domain

import com.rosario.juridico.marketplace.model.NivelVerificacion
import com.rosario.juridico.platform.FirebaseProxy
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.serialization.Serializable

/**
 * Modelo de datos blindado tolerante a documentos incompletos o variaciones en Firestore.
 */
@Serializable
data class ConsultaDataModel(
    val id: String? = "",
    val userId: String? = "",
    val categoria: String? = null,
    val descripcion: String? = "",
    val estado: String? = "pendiente",
    val fecha: String? = Clock.System.now().toString(),
    val motivoRechazo: String? = null,
    val telefono: String? = "",
    val ubicacion: String? = "",
    val urgencia: String? = "Media",
    val tipoConsulta: String? = "cliente",
    val subtipoB2B: String? = null,
    val jurisdiccionDestino: String? = null,
    val modalidadHonorarios: String? = null
)

/**
 * Resultado de la operación de publicación de consulta.
 */
sealed class PublicarConsultaResult {
    data class Success(val consultaId: String) : PublicarConsultaResult()
    data class Error(val message: String, val cause: Throwable? = null) : PublicarConsultaResult()
}

/**
 * Repositorio responsable de publicar y consultar las publicaciones de la colección 'consultas' en Firestore.
 */
class PublicarConsultaRepository {

    /**
     * Guarda la consulta legal en Firestore asociándola al cliente autenticado.
     */
    suspend fun publicarConsultaLegal(
        categoria: String? = "General",
        descripcion: String,
        telefono: String = "",
        ubicacion: String = "",
        urgencia: String = "Media",
        tipoConsulta: String = "cliente",
        subtipoB2B: String? = null,
        jurisdiccionDestino: String? = null,
        modalidadHonorarios: String? = null
    ): PublicarConsultaResult = withContext(Dispatchers.Default) {
        val userId = FirebaseProxy.getCurrentUserId()
            ?: return@withContext PublicarConsultaResult.Error(
                message = "Sesión no válida. Inicia sesión para publicar una consulta.",
            )

        if (FirebaseProxy.isAnonymous()) {
            return@withContext PublicarConsultaResult.Error(
                message = "Debes contar con una cuenta registrada para publicar consultas.",
            )
        }

        try {
            val firestore = Firebase.firestore

            // 🔒 TRUST & SAFETY: Sellar la publicación B2B validando Nivel 3 Verificado en la capa de datos
            if (tipoConsulta == "colegiado") {
                val perfilDoc = try { firestore.collection("perfiles_abogados").document(userId).get() } catch (_: Exception) { null }
                val abogDoc = if (perfilDoc?.exists != true) {
                    try { firestore.collection("abogados").document(userId).get() } catch (_: Exception) { null }
                } else null

                val perfilNivel = if (perfilDoc?.exists == true) try { perfilDoc.get<String?>("nivelVerificacion") } catch (_: Exception) { null } else null
                val abogNivel = if (abogDoc?.exists == true) try { abogDoc.get<String?>("nivelVerificacion") } catch (_: Exception) { null } else null

                val finalNivel = perfilNivel ?: abogNivel ?: ""

                if (finalNivel != NivelVerificacion.NIVEL_3_VERIFICADO.name) {
                    return@withContext PublicarConsultaResult.Error(
                        message = "Acceso denegado: Se requiere contar con perfil verificado (Nivel 3 Verificado) para publicar corresponsalías o interconsultas B2B."
                    )
                }
            }

            val collectionRef = firestore.collection("consultas")
            val newDocRef = collectionRef.document

            val nuevaConsulta = ConsultaDataModel(
                id = newDocRef.id,
                userId = userId,
                categoria = categoria,
                descripcion = descripcion,
                telefono = telefono,
                ubicacion = ubicacion,
                urgencia = urgencia,
                estado = "pendiente",
                fecha = Clock.System.now().toString(),
                tipoConsulta = tipoConsulta,
                subtipoB2B = subtipoB2B,
                jurisdiccionDestino = jurisdiccionDestino,
                modalidadHonorarios = modalidadHonorarios
            )

            newDocRef.set(nuevaConsulta)

            PublicarConsultaResult.Success(consultaId = newDocRef.id)
        } catch (e: Exception) {
            println("Error al intentar guardar la consulta en Firestore: ${e.message}")
            PublicarConsultaResult.Error(
                message = e.message ?: "Error de red al intentar guardar la consulta en Firestore.",
                cause = e,
            )
        }
    }

    /**
     * Consulta el feed de publicaciones en Firestore ordenadas por fecha reciente.
     */
    suspend fun getConsultasAbiertas(tipoConsultaFilter: String? = null): List<ConsultaDataModel> = withContext(Dispatchers.Default) {
        try {
            val firestore = Firebase.firestore
            val collection = firestore.collection("consultas")
            val snapshot = collection.get()

            snapshot.documents
                .mapNotNull { doc ->
                    try {
                        doc.data<ConsultaDataModel>()
                    } catch (e: Exception) {
                        println("Error crítico deserializando doc ${doc.id}: ${e.message}")
                        null
                    }
                }
                .filter { tipoConsultaFilter == null || (it.tipoConsulta ?: "cliente") == tipoConsultaFilter }
                .sortedByDescending { it.fecha ?: "" }
        } catch (e: Exception) {
            println("Fallo de red o permisos Firestore: ${e.message}")
            emptyList()
        }
    }
}
